import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { EDITOR_LIST_LOCAL_STORAGE_KEY } from '@Services/EditorList';
import {
  addEmptyProcesses,
  setInitialEditor,
  setInitialProcess,
  useEditor,
  addEmptyProcessesHighLight,
  resetHilightProcess,
  setSelectedProcessId,
} from '@Services/Editor';
import { useSelector, useDispatch } from 'react-redux';

import { getItem } from 'Utils/LocalStorage';
import { getProjectEvent } from '@Services/Editor/Request';
import SnackBar from '@Components/Common/SnackBar';

import useNetwork from '@Hooks/useNetwork';

import ErrorBoundary from '@Components/Common/ErrorBoundary';
import NetworkOfflineModal from '@Components/Editor/NetworkOfflineModal';

import Header from '@Components/Editor/Header';

import PrevIconSVG from '@Assets/Svgs/ic_prev.svg';
import NextIconSVG from '@Assets/Svgs/ic_next.svg';
import PrevFastIconSVG from '@Assets/Svgs/ic_prev_fast.svg';
import NextFastIconSVG from '@Assets/Svgs/ic_next_fast.svg';
import EnterIconSVG from '@Assets/Svgs/ic_return.svg';

import PageMoveModal from '@Components/Editor/PageMoveModal';
import AddImage from '@Assets/Images/ic_add.png';

import SideBarNextImage from '@Assets/Images/ic_next_double.png';
import SideBarPrevImage from '@Assets/Images/ic_prev_double.png';

import SideBar from './SideBar';
import Main from './Main';
import Achievement from './Achievement';

const DEFAULT_ERROR_MESSAGE = '새로 고침 바랍니다.';

const GET_PROJECT_EVENT_ERROR_MESSAGE = {
  S3_ERROR: 's3 error',
};

const Editor = () => {
  const dispatch = useDispatch();
  const snackBarRef = useRef(null);

  const isNetworkConnected = useNetwork();

  // ---------- paginations
  const { processes } = useSelector(useEditor);
  const limit = 20;
  const [page, setPage] = useState(1);
  const offset = (page - 1) * limit;
  // ----------- pagination
  const processRefs = useRef([]);
  const [isDataFetching, setIsDataFetching] = useState(false);
  const [searchParams] = useSearchParams();
  const projectIdByParams = searchParams.get('id');
  const [searchPid, setSearchPid] = useState();
  const [sideBarActive, setSideBarActive] = useState(true);
  const addToProcessRefs = element => {
    if (element && !processRefs.current.some(ref => ref?.id === element?.id)) {
      processRefs.current.push(element);
    }
  };

  const scrollToTargetProcess = targetProcessId => {
    const ele = processRefs.current.find(element => element.id === targetProcessId.toString());
    ele.scrollIntoView();
  };

  const searchPidFunc = targetPid => {
    const findProcess = processes.find(({ processId }) => processId === Number(targetPid));

    if (findProcess) {
      const groupNumber = Math.floor(findProcess.processId / 20) + 1;

      if (page !== groupNumber) {
        processRefs.current = [];
      }
      dispatch(setSelectedProcessId({ processId: findProcess.processId }));

      setPage(groupNumber);
      setTimeout(() => {
        scrollToTargetProcess(findProcess.processId);
      }, 500); // 500 밀리초 = 0.5초
    } else {
      snackBarRef.current.show({
        text: '없는 페이지입니다.',
      });
    }
  };

  const updateInitialEditor = useCallback(async () => {
    const targetEditorInLocalStorage = await getItem(EDITOR_LIST_LOCAL_STORAGE_KEY)?.[projectIdByParams];
    setIsDataFetching(true);

    try {
      const { projectEventData: editor } = await getProjectEvent(projectIdByParams);

      if (!targetEditorInLocalStorage) {
        // console.log('로컬스코리지가 없을 때!!!!');
        editor.projectId = projectIdByParams;
        dispatch(setInitialEditor({ editor }));
      } else if (editor?.updatedAt > targetEditorInLocalStorage?.updatedAt) {
        // console.log('서버에서 가져온 데이터가 로컬스코리지 데이터보다 최신일 때!!!!');
        editor.projectId = projectIdByParams;
        dispatch(setInitialEditor({ editor }));
      } else if (
        !(editor?.updatedAt > targetEditorInLocalStorage?.updatedAt) &&
        editor?.updatedAt === targetEditorInLocalStorage?.updatedAt
      ) {
        // console.log('서버에서 가져온 데이터가 로컬스코리지 데이터와 같을 때!!!!');
        dispatch(setInitialEditor({ editor: targetEditorInLocalStorage }));
      }
    } catch (error) {
      console.log('error:', error);
      const { msg } = error;

      if (msg === GET_PROJECT_EVENT_ERROR_MESSAGE.S3_ERROR) {
        dispatch(setInitialEditor({ projectId: projectIdByParams }));
      }
    }

    setIsDataFetching(false);

    // if (targetEditorInLocalStorage) {
    //   dispatch(setInitialEditor({ editor: targetEditorInLocalStorage }));
    // } else {
    //   try {
    //     // const { projectEventData: editor } = await getProjectEvent(projectIdByParams);
    // editor.projectId = projectIdByParams;
    // dispatch(setInitialEditor({ editor }));
    //   } catch (error) {
    // const { msg } = error;

    // if (msg === GET_PROJECT_EVENT_ERROR_MESSAGE.S3_ERROR) {
    //   dispatch(setInitialEditor({ projectId: projectIdByParams }));
    // }
    //   }
    // }

    setIsDataFetching(false);
  }, [dispatch, projectIdByParams]);

  // const updateInitialEditor = useCallback(async () => {
  //   const targetEditorInLocalStorage = await getItem(EDITOR_LIST_LOCAL_STORAGE_KEY)?.[projectIdByParams];

  //   setIsDataFetching(true);

  //   if (targetEditorInLocalStorage) {
  //     dispatch(setInitialEditor({ editor: targetEditorInLocalStorage }));
  //   } else {
  //     try {
  //       const { projectEventData: editor } = await getProjectEvent(projectIdByParams);
  //       editor.projectId = projectIdByParams;
  //       dispatch(setInitialEditor({ editor }));
  //     } catch (error) {
  //       const { msg } = error;

  //       if (msg === GET_PROJECT_EVENT_ERROR_MESSAGE.S3_ERROR) {
  //         dispatch(setInitialEditor({ projectId: projectIdByParams }));
  //       }
  //     }
  //   }

  //   setIsDataFetching(false);
  // }, [dispatch, projectIdByParams]);

  useEffect(() => {
    const onBeforeClosed = event => {
      event.preventDefault();
      // eslint-disable-next-line no-param-reassign
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', onBeforeClosed, false);

    return () => window.removeEventListener('beforeunload', onBeforeClosed, false);
  }, []);

  useEffect(() => {
    processRefs.current = [];
    updateInitialEditor();
  }, []);

  useEffect(() => {
    return () => {
      dispatch(setInitialProcess());
    };
  }, []);

  const memoizedReactPaginateCompo = useMemo(() => {
    const total = processes.length;
    const numPages = Math.ceil(total / limit);
    // const currentPageGroup = Math.ceil(page / 10); // 현재 페이지 그룹 계산

    // const startPage = (currentPageGroup - 1) * 10 + 1; // 시작 페이지 계산
    // const endPage = Math.min(currentPageGroup * 10, numPages); // 끝 페이지 계산

    const enablePrev = page !== 1;
    const enablePrevFast = page - 100 >= 0;

    const enableNext = page !== numPages;
    const enableNextFast = page + 100 <= total;
    const showFastIcons = total > 100;

    // const pageFuc = pageNumber => {
    //   processRefs.current = [];
    //   setPage(pageNumber);
    // };

    const prevAndNextFunc = type => {
      processRefs.current = [];

      if (type === 'prev') {
        if (!enablePrev) return;

        setPage(page - 1);
      } else if (type === 'next') {
        if (!enableNext) return;

        setPage(page + 1);
      }
    };

    const prevAndNextFunc2 = type => {
      processRefs.current = [];

      if (type === 'prev') {
        if (!enablePrev) return;

        if (page - 10 < 1) {
          setPage(1);
        } else {
          setPage(page - 10);
        }
      } else if (type === 'next') {
        if (!enableNext) return;

        if (page + 10 > numPages) {
          setPage(numPages);
        } else {
          setPage(page + 10);
        }
      }
    };

    const onChanageSearchPid = event => {
      setSearchPid(event.target.value);
      if (event.key === 'Enter') {
        searchPidFunc(event.target.value);
      }
    };

    // const searchPidFunc = targetPid => {
    //   console.log('targetPid:', targetPid);

    //   const findProcess = processes.find(({ processId }) => processId === Number(targetPid));

    //   if (findProcess) {
    //     const groupNumber = Math.floor(findProcess.processId / 10) + 1;
    //     setPage(groupNumber);

    //     // scrollToTargetProcess(findProcess.processId);
    //     setTimeout(() => {
    //       scrollToTargetProcess(findProcess.processId);
    //     }, 500); // 500 밀리초 = 0.5초
    //   } else {
    //     alert('해당하는 pid가 없습니다.');
    //   }
    // };

    return (
      <PagiNateContainer>
        {/* 페이지 그룹의 시작부터 끝까지의 페이지 번호 표시 */}
        {/* <PageWrapper>
          {Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index).map(pageNumber => {
            return (
              <PageNumberBtn
                key={pageNumber}
                // onClick={() => setPage(pageNumber)}

                onClick={() => pageFuc(pageNumber)}
                aria-current={page === pageNumber ? 'page' : undefined}
              >
                <div
                  style={{
                    width: '25px',
                  }}
                >
                  {pageNumber}
                </div>
              </PageNumberBtn>
            );
          })}
        </PageWrapper> */}

        <BottomContainer>
          <PidSearch>
            <PidInput
              type="number"
              name="searchPid"
              value={searchPid}
              onChange={event => setSearchPid(event.target.value)}
              onKeyPress={onChanageSearchPid}
              placeholder="P."
            />
            <SearchBtn onClick={() => searchPidFunc(searchPid)}>
              <SearchBtnIcon active={searchPid > 0} />
            </SearchBtn>
          </PidSearch>
          <PageBtn
            // onClick={() => notShowPrev && setPage(page - 1)}
            onClick={() => prevAndNextFunc2('prev')}
            disabled={!enablePrevFast}
            isShow={showFastIcons}
          >
            <PrevFastBtnIcon active={enablePrevFast} />
          </PageBtn>
          <PageBtn
            // onClick={() => notShowPrev && setPage(page - 1)}
            onClick={() => prevAndNextFunc('prev')}
            disabled={!enablePrev}
          >
            <PrevBtnIcon active={enablePrev} />
          </PageBtn>

          <PageBtn
            // onClick={() => notShowNext && setPage(page + 1)}
            onClick={() => prevAndNextFunc('next')}
            disabled={!enableNext}
          >
            <NextBtnIcon active={enableNext} />
          </PageBtn>
          <PageBtn
            // onClick={() => notShowNext && setPage(page + 1)}
            onClick={() => prevAndNextFunc2('next')}
            disabled={!enableNextFast}
            isShow={showFastIcons}
          >
            <NextFastBtnIcon active={enableNextFast} />
          </PageBtn>
        </BottomContainer>
      </PagiNateContainer>
    );
  }, [processes, limit, page, processRefs, searchPid]); // 의존성 배열에 handlePageClick과 pageCount를 추가합니다.

  // const searchPidFunc = targetPid => {
  //   console.log('targetPid:', targetPid);

  //   const findProcess = processes.find(({ processId }) => processId === Number(targetPid));

  //   if (findProcess) {
  //     const groupNumber = Math.floor(findProcess.processId / 10) + 1;
  //     setPage(groupNumber);

  //     // scrollToTargetProcess(findProcess.processId);
  //     setTimeout(() => {
  //       scrollToTargetProcess(findProcess.processId);
  //     }, 500); // 500 밀리초 = 0.5초
  //   } else {
  //     alert('해당하는 pid가 없습니다.');
  //   }
  // };

  const onAddEmptyProcesses = useCallback(() => {
    if (processes?.length >= 500) {
      snackBarRef.current.show({
        text: '페이지는 최대 500개까지 생성 가능합니다.',
      });

      return;
    }
    dispatch(addEmptyProcesses());

    const total = processes.length;
    const numPages = Math.ceil(total / limit);

    const lastProcess = processes[processes.length - 1];

    if ((lastProcess.processId + 1) % 20 === 0) {
      setPage(numPages + 1);
    } else {
      setPage(numPages);
    }

    setTimeout(() => {
      scrollToTargetProcess(lastProcess.processId);
      dispatch(addEmptyProcessesHighLight({ processId: lastProcess.processId }));

      setTimeout(() => {
        dispatch(resetHilightProcess());
      }, 1000); // 1초 후에 resetHilightProcess 액션 디스패치
    }, 500); // 500 밀리초 = 0.5초
  }, [dispatch, processes, limit]);

  const currentItems = processes?.slice(offset, offset + limit);

  return (
    processes &&
    currentItems && (
      <ErrorBoundary errorMessage={DEFAULT_ERROR_MESSAGE}>
        <EditorContainer>
          <SideBarContainer active={sideBarActive}>
            <SideBar
              isDataFetching={isDataFetching}
              projectId={projectIdByParams}
              scrollToTargetProcess={scrollToTargetProcess}
              ReactPaginateCompo={() => memoizedReactPaginateCompo}
              currentItems={currentItems}
              searchPidFunc={searchPidFunc}
            />
          </SideBarContainer>

          <SideBarButtonContainer onClick={() => setSideBarActive(!sideBarActive)}>
            {sideBarActive ? (
              <SideBarImage style={{}} src={SideBarPrevImage} alt="sideBarNextImage" />
            ) : (
              <SideBarImage src={SideBarNextImage} alt="sideBarPrevImage" />
            )}
          </SideBarButtonContainer>
          <MainContainer>
            <Header isNetworkConnected={isNetworkConnected} projectId={projectIdByParams} />
            <Main
              isDataFetching={isDataFetching}
              processRefs={processRefs}
              addToProcessRefs={addToProcessRefs}
              scrollToTargetProcess={scrollToTargetProcess}
              snackBarRef={snackBarRef}
              currentItems={currentItems}
              offset={offset}
              limit={limit}
              setPage={setPage}
            />
          </MainContainer>

          <Achievement />
        </EditorContainer>

        <NetworkOfflineModal isNetworkConnected={isNetworkConnected} />

        <PageMoveModal
          processes={processes}
          snackBarRef={snackBarRef}
          scrollToTargetProcess={scrollToTargetProcess}
          setPage={setPage}
        />
        <SnackBar ref={snackBarRef} />
        {/* <div
          style={{
            position: 'fixed',
            right: '0',
            bottom: '0',
            width: '100px',
            height: '100px',
            backgroundColor: 'red',
          }}
        > */}
        <AddingProcessEndButton onClick={onAddEmptyProcesses}>
          <AddPageIcon />
        </AddingProcessEndButton>
      </ErrorBoundary>
    )
  );
};

const EditorContainer = styled.div`
  display: flex;

  height: 100vh;
  min-height: 100vh;
`;

const MainContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;

  height: 100vh;
  min-height: 100vh;

  /* background: ${({ theme }) => theme.colors.white}; */
  /* background-color: lavender; */
  padding-bottom: 40px;
`;

const PagiNateContainer = styled.nav`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  border-radius: 10px;
`;

// const PageNumberBtn = styled.div`
//   margin: 2px;
//   margin-right: 5px;
//   margin-left: 5px;
//   color: gray;
//   padding: 5px;
//   font-size: 16px;
//   cursor: pointer;
//   width: 25px;
//   text-align: center;

//   &:hover {
//   }

//   &[disabled] {
//   }

//   &[aria-current] {
//     color: black;
//     font-weight: bold;
//   }
// `;

// const PageWrapper = styled.div`
//   display: flex;
//   flex-wrap: wrap;
//   width: 100%;
//   /* padding-left: 10px; */
//   /* padding-right: 10px; */
// `;

const IconCSS = css`
  height: 16px;
  opacity: ${({ active }) => (active ? 1 : 0.5)};
`;

const PidSearch = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.ui_3};
  height: 24px;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  margin-right: 8px;
`;

const PidInput = styled.input`
  width: calc(100% - 32px);
  height: 30px;
  border: none;
  padding-left: 8px;
  background-color: transparent;
  outline: none;
  user-select: none;
  /* Chrome, Safari, Edge, Opera */
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  &[type='number'] {
    -moz-appearance: textfield;
  }
`;

const SearchBtn = styled.button`
  right: 0 !important;
  width: 28x;
  height: 32px;
  border: none;
  background-color: transparent;
`;

const BottomContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: calc(100%+50px);
  align-items: center;
  justify-content: center;
`;

const PageBtn = styled.div`
  cursor: pointer;
  width: 24px !important;
  height: 32px !important;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  display: ${({ isShow = true }) => (isShow ? 'flex' : 'none')};
`;

const SearchBtnIcon = styled.img.attrs({ src: EnterIconSVG, alt: '검색' })`
  ${IconCSS}
  opacity: ${({ active }) => (active ? 1 : 0)};
`;

const PrevBtnIcon = styled.img.attrs({ src: PrevIconSVG, alt: '10페이지 전으로' })`
  ${IconCSS}
`;

const PrevFastBtnIcon = styled.img.attrs({ src: PrevFastIconSVG, alt: '100페이지 전으로' })`
  ${IconCSS}
`;

const NextBtnIcon = styled.img.attrs({ src: NextIconSVG, alt: '10페이지 후로' })`
  ${IconCSS}
`;

const NextFastBtnIcon = styled.img.attrs({ src: NextFastIconSVG, alt: '100페이지 후로' })`
  ${IconCSS}
`;

const AddingProcessEndButton = styled.div`
  position: fixed;
  right: 0;
  bottom: 0;

  display: flex;
  width: 48px;
  height: 48px;
  padding: 16px;
  justify-content: center;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
  border-radius: 32px;
  border: 1px solid var(--ui_2, #f2f2f3);
  background: var(--ui_1, #fcfcfd);
  box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.1);
  margin-right: 39px;
  margin-bottom: 36px;
  cursor: pointer;
`;

const AddPageIcon = styled.img.attrs({
  src: AddImage,
  alt: '페이지 끝에 추가',
})`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
`;

const SideBarButtonContainer = styled.div`
  height: 30px;
  padding: 10px;
  border-radius: 10px;
  display: flex;
  justify-content: cetner;
  align-items: center;
  margin-top: 17px;
  margin-left: 5px;
`;

const SideBarContainer = styled.div`
  width: ${({ active }) => (active ? '250px' : '0')};
  transition: width 0.3s ease;
  overflow: hidden;
`;

const SideBarImage = styled.img`
  width: 20px;
  height: 20px;
`;

export default React.memo(Editor);

// import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
// import { useSearchParams } from 'react-router-dom';
// import styled, { css } from 'styled-components';
// import { EDITOR_LIST_LOCAL_STORAGE_KEY } from '@Services/EditorList';
// import {
//   addEmptyProcesses,
//   setInitialEditor,
//   setInitialProcess,
//   useEditor,
//   addEmptyProcessesHighLight,
//   resetHilightProcess,
// } from '@Services/Editor';
// import { useSelector, useDispatch } from 'react-redux';

// import { getItem } from 'Utils/LocalStorage';
// import { getProjectEvent } from '@Services/Editor/Request';
// import SnackBar from '@Components/Common/SnackBar';

// import useNetwork from '@Hooks/useNetwork';

// import ErrorBoundary from '@Components/Common/ErrorBoundary';
// import NetworkOfflineModal from '@Components/Editor/NetworkOfflineModal';

// import Header from '@Components/Editor/Header';

// import PrevIconSVG from '@Assets/Svgs/ic_prev.svg';
// import NextIconSVG from '@Assets/Svgs/ic_next.svg';
// import PrevFastIconSVG from '@Assets/Svgs/ic_prev_fast.svg';
// import NextFastIconSVG from '@Assets/Svgs/ic_next_fast.svg';
// import EnterIconSVG from '@Assets/Svgs/ic_return.svg';

// import PageMoveModal from '@Components/Editor/PageMoveModal';
// import AddImage from '@Assets/Images/ic_add.png';

// import SideBarNextImage from '@Assets/Images/ic_next_double.png';
// import SideBarPrevImage from '@Assets/Images/ic_prev_double.png';

// import SideBar from './SideBar';
// import Main from './Main';
// import Achievement from './Achievement';

// const DEFAULT_ERROR_MESSAGE = '새로 고침 바랍니다.';

// const GET_PROJECT_EVENT_ERROR_MESSAGE = {
//   S3_ERROR: 's3 error',
// };

// const Editor = () => {
//   const dispatch = useDispatch();
//   const snackBarRef = useRef(null);

//   const isNetworkConnected = useNetwork();

//   // ---------- paginations
//   const { processes } = useSelector(useEditor);
//   const limit = 20;
//   const [page, setPage] = useState(1);
//   const offset = (page - 1) * limit;
//   // ----------- pagination
//   const processRefs = useRef([]);
//   const [isDataFetching, setIsDataFetching] = useState(false);
//   const [searchParams] = useSearchParams();
//   const projectIdByParams = searchParams.get('id');
//   const [searchPid, setSearchPid] = useState();
//   const [sideBarActive, setSideBarActive] = useState(true);

//   const addToProcessRefs = element => {
//     if (element && !processRefs.current.includes(element)) {
//       processRefs.current.push(element);
//     }
//   };

//   const scrollToTargetProcess = targetProcessId => {
//     const ele = processRefs.current.find(element => element.id === targetProcessId.toString());

//     ele.scrollIntoView();
//   };

//   const searchPidFunc = targetPid => {
//     const findProcess = processes.find(({ processId }) => processId === Number(targetPid));

//     if (findProcess) {
//       const groupNumber = Math.floor(findProcess.processId / 20) + 1;
//       setPage(groupNumber);

//       // scrollToTargetProcess(findProcess.processId);
//       setTimeout(() => {
//         scrollToTargetProcess(findProcess.processId);
//       }, 500); // 500 밀리초 = 0.5초
//     } else {
//       snackBarRef.current.show({
//         text: '없는 페이지입니다.',
//       });
//     }
//   };

//   const updateInitialEditor = useCallback(async () => {
//     const targetEditorInLocalStorage = await getItem(EDITOR_LIST_LOCAL_STORAGE_KEY)?.[projectIdByParams];
//     setIsDataFetching(true);

//     try {
//       const { projectEventData: editor } = await getProjectEvent(projectIdByParams);
//       // console.log('editor:', editor);
//       // console.log('targetEditorInLocalStorage:', targetEditorInLocalStorage);

//       // console.log('editor?.updatedAt:', editor?.updatedAt);
//       // console.log('targetEditorInLocalStorage?.updatedAt:', targetEditorInLocalStorage?.updatedAt);

//       if (!targetEditorInLocalStorage) {
//         // console.log('로컬스코리지가 없을 때!!!!');
//         editor.projectId = projectIdByParams;
//         dispatch(setInitialEditor({ editor }));
//       } else if (editor?.updatedAt > targetEditorInLocalStorage?.updatedAt) {
//         // console.log('서버에서 가져온 데이터가 로컬스코리지 데이터보다 최신일 때!!!!');
//         editor.projectId = projectIdByParams;
//         dispatch(setInitialEditor({ editor }));
//       } else if (
//         !(editor?.updatedAt > targetEditorInLocalStorage?.updatedAt) &&
//         editor?.updatedAt === targetEditorInLocalStorage?.updatedAt
//       ) {
//         // console.log('서버에서 가져온 데이터가 로컬스코리지 데이터와 같을 때!!!!');
//         dispatch(setInitialEditor({ editor: targetEditorInLocalStorage }));
//       }
//     } catch (error) {
//       console.log('error:', error);
//       const { msg } = error;

//       if (msg === GET_PROJECT_EVENT_ERROR_MESSAGE.S3_ERROR) {
//         dispatch(setInitialEditor({ projectId: projectIdByParams }));
//       }
//     }

//     setIsDataFetching(false);

//     // if (targetEditorInLocalStorage) {
//     //   dispatch(setInitialEditor({ editor: targetEditorInLocalStorage }));
//     // } else {
//     //   try {
//     //     // const { projectEventData: editor } = await getProjectEvent(projectIdByParams);
//     // editor.projectId = projectIdByParams;
//     // dispatch(setInitialEditor({ editor }));
//     //   } catch (error) {
//     // const { msg } = error;

//     // if (msg === GET_PROJECT_EVENT_ERROR_MESSAGE.S3_ERROR) {
//     //   dispatch(setInitialEditor({ projectId: projectIdByParams }));
//     // }
//     //   }
//     // }

//     setIsDataFetching(false);
//   }, [dispatch, projectIdByParams]);

//   // const updateInitialEditor = useCallback(async () => {
//   //   const targetEditorInLocalStorage = await getItem(EDITOR_LIST_LOCAL_STORAGE_KEY)?.[projectIdByParams];

//   //   setIsDataFetching(true);

//   //   if (targetEditorInLocalStorage) {
//   //     dispatch(setInitialEditor({ editor: targetEditorInLocalStorage }));
//   //   } else {
//   //     try {
//   //       const { projectEventData: editor } = await getProjectEvent(projectIdByParams);
//   //       editor.projectId = projectIdByParams;
//   //       dispatch(setInitialEditor({ editor }));
//   //     } catch (error) {
//   //       const { msg } = error;

//   //       if (msg === GET_PROJECT_EVENT_ERROR_MESSAGE.S3_ERROR) {
//   //         dispatch(setInitialEditor({ projectId: projectIdByParams }));
//   //       }
//   //     }
//   //   }

//   //   setIsDataFetching(false);
//   // }, [dispatch, projectIdByParams]);

//   useEffect(() => {
//     const onBeforeClosed = event => {
//       event.preventDefault();
//       // eslint-disable-next-line no-param-reassign
//       event.returnValue = '';
//     };

//     window.addEventListener('beforeunload', onBeforeClosed, false);

//     return () => window.removeEventListener('beforeunload', onBeforeClosed, false);
//   }, []);

//   useEffect(() => {
//     processRefs.current = [];
//     updateInitialEditor();
//   }, []);

//   useEffect(() => {
//     return () => {
//       dispatch(setInitialProcess());
//     };
//   }, []);

//   const memoizedReactPaginateCompo = useMemo(() => {
//     const total = processes.length;
//     const numPages = Math.ceil(total / limit);
//     // const currentPageGroup = Math.ceil(page / 10); // 현재 페이지 그룹 계산

//     // const startPage = (currentPageGroup - 1) * 10 + 1; // 시작 페이지 계산
//     // const endPage = Math.min(currentPageGroup * 10, numPages); // 끝 페이지 계산

//     const enablePrev = page !== 1;
//     const enablePrevFast = page - 100 >= 0;

//     const enableNext = page !== numPages;
//     const enableNextFast = page + 100 <= total;
//     const showFastIcons = total > 100;

//     // const pageFuc = pageNumber => {
//     //   processRefs.current = [];
//     //   setPage(pageNumber);
//     // };

//     const prevAndNextFunc = type => {
//       processRefs.current = [];

//       if (type === 'prev') {
//         if (!enablePrev) return;

//         setPage(page - 1);
//       } else if (type === 'next') {
//         if (!enableNext) return;

//         setPage(page + 1);
//       }
//     };

//     const prevAndNextFunc2 = type => {
//       processRefs.current = [];

//       if (type === 'prev') {
//         if (!enablePrev) return;

//         if (page - 10 < 1) {
//           setPage(1);
//         } else {
//           setPage(page - 10);
//         }
//       } else if (type === 'next') {
//         if (!enableNext) return;

//         if (page + 10 > numPages) {
//           setPage(numPages);
//         } else {
//           setPage(page + 10);
//         }
//       }
//     };

//     const onChanageSearchPid = event => {
//       setSearchPid(event.target.value);
//       if (event.key === 'Enter') {
//         searchPidFunc(event.target.value);
//       }
//     };

//     // const searchPidFunc = targetPid => {
//     //   console.log('targetPid:', targetPid);

//     //   const findProcess = processes.find(({ processId }) => processId === Number(targetPid));

//     //   if (findProcess) {
//     //     const groupNumber = Math.floor(findProcess.processId / 10) + 1;
//     //     setPage(groupNumber);

//     //     // scrollToTargetProcess(findProcess.processId);
//     //     setTimeout(() => {
//     //       scrollToTargetProcess(findProcess.processId);
//     //     }, 500); // 500 밀리초 = 0.5초
//     //   } else {
//     //     alert('해당하는 pid가 없습니다.');
//     //   }
//     // };

//     return (
//       <PagiNateContainer>
//         {/* 페이지 그룹의 시작부터 끝까지의 페이지 번호 표시 */}
//         {/* <PageWrapper>
//           {Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index).map(pageNumber => {
//             return (
//               <PageNumberBtn
//                 key={pageNumber}
//                 // onClick={() => setPage(pageNumber)}

//                 onClick={() => pageFuc(pageNumber)}
//                 aria-current={page === pageNumber ? 'page' : undefined}
//               >
//                 <div
//                   style={{
//                     width: '25px',
//                   }}
//                 >
//                   {pageNumber}
//                 </div>
//               </PageNumberBtn>
//             );
//           })}
//         </PageWrapper> */}

//         <BottomContainer>
//           <PidSearch>
//             <PidInput
//               type="number"
//               name="searchPid"
//               value={searchPid}
//               onChange={event => setSearchPid(event.target.value)}
//               onKeyPress={onChanageSearchPid}
//               placeholder="P."
//             />
//             <SearchBtn onClick={() => searchPidFunc(searchPid)}>
//               <SearchBtnIcon active={searchPid > 0} />
//             </SearchBtn>
//           </PidSearch>
//           <PageBtn
//             // onClick={() => notShowPrev && setPage(page - 1)}
//             onClick={() => prevAndNextFunc2('prev')}
//             disabled={!enablePrevFast}
//             isShow={showFastIcons}
//           >
//             <PrevFastBtnIcon active={enablePrevFast} />
//           </PageBtn>
//           <PageBtn
//             // onClick={() => notShowPrev && setPage(page - 1)}
//             onClick={() => prevAndNextFunc('prev')}
//             disabled={!enablePrev}
//           >
//             <PrevBtnIcon active={enablePrev} />
//           </PageBtn>

//           <PageBtn
//             // onClick={() => notShowNext && setPage(page + 1)}
//             onClick={() => prevAndNextFunc('next')}
//             disabled={!enableNext}
//           >
//             <NextBtnIcon active={enableNext} />
//           </PageBtn>
//           <PageBtn
//             // onClick={() => notShowNext && setPage(page + 1)}
//             onClick={() => prevAndNextFunc2('next')}
//             disabled={!enableNextFast}
//             isShow={showFastIcons}
//           >
//             <NextFastBtnIcon active={enableNextFast} />
//           </PageBtn>
//         </BottomContainer>
//       </PagiNateContainer>
//     );
//   }, [processes, limit, page, processRefs, searchPid]); // 의존성 배열에 handlePageClick과 pageCount를 추가합니다.

//   // const searchPidFunc = targetPid => {
//   //   console.log('targetPid:', targetPid);

//   //   const findProcess = processes.find(({ processId }) => processId === Number(targetPid));

//   //   if (findProcess) {
//   //     const groupNumber = Math.floor(findProcess.processId / 10) + 1;
//   //     setPage(groupNumber);

//   //     // scrollToTargetProcess(findProcess.processId);
//   //     setTimeout(() => {
//   //       scrollToTargetProcess(findProcess.processId);
//   //     }, 500); // 500 밀리초 = 0.5초
//   //   } else {
//   //     alert('해당하는 pid가 없습니다.');
//   //   }
//   // };

//   const onAddEmptyProcesses = useCallback(() => {
//     if (processes?.length >= 500) {
//       snackBarRef.current.show({
//         text: '페이지는 최대 500개까지 생성 가능합니다.',
//       });

//       return;
//     }
//     dispatch(addEmptyProcesses());

//     const total = processes.length;
//     const numPages = Math.ceil(total / limit);

//     const lastProcess = processes[processes.length - 1];

//     if ((lastProcess.processId + 1) % 20 === 0) {
//       setPage(numPages + 1);
//     } else {
//       setPage(numPages);
//     }

//     setTimeout(() => {
//       scrollToTargetProcess(lastProcess.processId);
//       dispatch(addEmptyProcessesHighLight({ processId: lastProcess.processId }));

//       setTimeout(() => {
//         dispatch(resetHilightProcess());
//       }, 1000); // 1초 후에 resetHilightProcess 액션 디스패치
//     }, 500); // 500 밀리초 = 0.5초
//   }, [dispatch, processes, limit]);

//   const currentItems = processes?.slice(offset, offset + limit);

//   return (
//     processes &&
//     currentItems && (
//       <ErrorBoundary errorMessage={DEFAULT_ERROR_MESSAGE}>
//         <EditorContainer>
//           <SideBarContainer active={sideBarActive}>
//             <SideBar
//               isDataFetching={isDataFetching}
//               projectId={projectIdByParams}
//               scrollToTargetProcess={scrollToTargetProcess}
//               ReactPaginateCompo={() => memoizedReactPaginateCompo}
//               currentItems={currentItems}
//               searchPidFunc={searchPidFunc}
//             />
//           </SideBarContainer>

//           <SideBarButtonContainer onClick={() => setSideBarActive(!sideBarActive)}>
//             {sideBarActive ? (
//               <SideBarImage style={{}} src={SideBarPrevImage} alt="sideBarNextImage" />
//             ) : (
//               <SideBarImage src={SideBarNextImage} alt="sideBarPrevImage" />
//             )}
//           </SideBarButtonContainer>
//           <MainContainer>
//             <Header isNetworkConnected={isNetworkConnected} projectId={projectIdByParams} />
//             <Main
//               isDataFetching={isDataFetching}
//               processRefs={processRefs}
//               addToProcessRefs={addToProcessRefs}
//               scrollToTargetProcess={scrollToTargetProcess}
//               snackBarRef={snackBarRef}
//               currentItems={currentItems}
//               offset={offset}
//               limit={limit}
//               setPage={setPage}
//             />
//           </MainContainer>

//           <Achievement />
//         </EditorContainer>

//         <NetworkOfflineModal isNetworkConnected={isNetworkConnected} />

//         <PageMoveModal
//           processes={processes}
//           snackBarRef={snackBarRef}
//           scrollToTargetProcess={scrollToTargetProcess}
//           setPage={setPage}
//         />
//         <SnackBar ref={snackBarRef} />
//         {/* <div
//           style={{
//             position: 'fixed',
//             right: '0',
//             bottom: '0',
//             width: '100px',
//             height: '100px',
//             backgroundColor: 'red',
//           }}
//         > */}
//         <AddingProcessEndButton onClick={onAddEmptyProcesses}>
//           <AddPageIcon />
//         </AddingProcessEndButton>
//       </ErrorBoundary>
//     )
//   );
// };

// const EditorContainer = styled.div`
//   display: flex;

//   height: 100vh;
//   min-height: 100vh;
// `;

// const MainContainer = styled.div`
//   flex: 1;
//   display: flex;
//   flex-direction: column;

//   height: 100vh;
//   min-height: 100vh;

//   /* background: ${({ theme }) => theme.colors.white}; */
//   /* background-color: lavender; */
//   padding-bottom: 40px;
// `;

// const PagiNateContainer = styled.nav`
//   display: flex;
//   flex-wrap: wrap;
//   width: 100%;
//   border-radius: 10px;
// `;

// // const PageNumberBtn = styled.div`
// //   margin: 2px;
// //   margin-right: 5px;
// //   margin-left: 5px;
// //   color: gray;
// //   padding: 5px;
// //   font-size: 16px;
// //   cursor: pointer;
// //   width: 25px;
// //   text-align: center;

// //   &:hover {
// //   }

// //   &[disabled] {
// //   }

// //   &[aria-current] {
// //     color: black;
// //     font-weight: bold;
// //   }
// // `;

// // const PageWrapper = styled.div`
// //   display: flex;
// //   flex-wrap: wrap;
// //   width: 100%;
// //   /* padding-left: 10px; */
// //   /* padding-right: 10px; */
// // `;

// const IconCSS = css`
//   height: 16px;
//   opacity: ${({ active }) => (active ? 1 : 0.5)};
// `;

// const PidSearch = styled.div`
//   width: 100%;
//   background-color: ${({ theme }) => theme.colors.ui_3};
//   height: 24px;
//   flex: 1;
//   display: flex;
//   align-items: center;
//   justify-content: center;
//   border-radius: 6px;
//   margin-right: 8px;
// `;

// const PidInput = styled.input`
//   width: calc(100% - 32px);
//   height: 30px;
//   border: none;
//   padding-left: 8px;
//   background-color: transparent;
//   outline: none;
//   user-select: none;
//   /* Chrome, Safari, Edge, Opera */
//   &::-webkit-inner-spin-button,
//   &::-webkit-outer-spin-button {
//     -webkit-appearance: none;
//     margin: 0;
//   }

//   /* Firefox */
//   &[type='number'] {
//     -moz-appearance: textfield;
//   }
// `;

// const SearchBtn = styled.button`
//   right: 0 !important;
//   width: 28x;
//   height: 32px;
//   border: none;
//   background-color: transparent;
// `;

// const BottomContainer = styled.div`
//   display: flex;
//   flex-direction: row;
//   width: calc(100%+50px);
//   align-items: center;
//   justify-content: center;
// `;

// const PageBtn = styled.div`
//   cursor: pointer;
//   width: 24px !important;
//   height: 32px !important;
//   display: flex;
//   flex-direction: row;
//   align-items: center;
//   justify-content: center;
//   display: ${({ isShow = true }) => (isShow ? 'flex' : 'none')};
// `;

// const SearchBtnIcon = styled.img.attrs({ src: EnterIconSVG, alt: '검색' })`
//   ${IconCSS}
//   opacity: ${({ active }) => (active ? 1 : 0)};
// `;

// const PrevBtnIcon = styled.img.attrs({ src: PrevIconSVG, alt: '10페이지 전으로' })`
//   ${IconCSS}
// `;

// const PrevFastBtnIcon = styled.img.attrs({ src: PrevFastIconSVG, alt: '100페이지 전으로' })`
//   ${IconCSS}
// `;

// const NextBtnIcon = styled.img.attrs({ src: NextIconSVG, alt: '10페이지 후로' })`
//   ${IconCSS}
// `;

// const NextFastBtnIcon = styled.img.attrs({ src: NextFastIconSVG, alt: '100페이지 후로' })`
//   ${IconCSS}
// `;

// const AddingProcessEndButton = styled.div`
//   position: fixed;
//   right: 0;
//   bottom: 0;

//   display: flex;
//   width: 48px;
//   height: 48px;
//   padding: 16px;
//   justify-content: center;
//   align-items: center;
//   gap: 16px;
//   flex-shrink: 0;
//   border-radius: 32px;
//   border: 1px solid var(--ui_2, #f2f2f3);
//   background: var(--ui_1, #fcfcfd);
//   box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.1);
//   margin-right: 39px;
//   margin-bottom: 36px;
//   cursor: pointer;
// `;

// const AddPageIcon = styled.img.attrs({
//   src: AddImage,
//   alt: '페이지 끝에 추가',
// })`
//   width: 20px;
//   height: 20px;
//   flex-shrink: 0;
// `;

// const SideBarButtonContainer = styled.div`
//   height: 30px;
//   padding: 10px;
//   border-radius: 10px;
//   display: flex;
//   justify-content: cetner;
//   align-items: center;
//   margin-top: 17px;
//   margin-left: 5px;
// `;

// const SideBarContainer = styled.div`
//   width: ${({ active }) => (active ? '250px' : '0')};
//   transition: width 0.3s ease;
//   overflow: hidden;
// `;

// const SideBarImage = styled.img`
//   width: 20px;
//   height: 20px;
// `;

// export default React.memo(Editor);
